import "./Hero.css";
import { FaLinkedin, FaGithub, FaCode } from "react-icons/fa";
import { SiLeetcode, SiGeeksforgeeks } from "react-icons/si";
import { useState, useEffect, useRef, memo, lazy, Suspense, useMemo } from "react";
import { images } from "../assets/images";

const LazyGitHubStats = lazy(() => import("./GitHubStats"));
import { HiddenDevComment } from "./DeveloperMode";
import { devComments } from "../config/devComments";
import { useDebug } from "../contexts/DebugContext";
import { LINKS } from "../config/links";
import ErrorBoundary from "./ErrorBoundary";

// Rotating professional roles displayed in the hero section
const roles = ["Senior Software Engineer", "Systems Architect", "AI Systems Builder"];

// Terminal-style typing effect content - developer command snippets.
// These lines are hardcoded terminal-style outputs that cycle forever.
// They are NOT dynamic -- they are a cosmetic effect only.
const typingLines = [
  'System.out.println("Hello, World!");',
  'git commit -m "feat: AI learning tools"',
  "SELECT * FROM devs WHERE role = 'Senior';",
  'console.log("shipped games to 25M+");',
  '@Service("matchmaker") //Spring Boot',
  '@RestController("/api/v1")',
  'git commit -m "feat: clan systems"',
  "git merge --no-ff release/v2.0",
  'git commit -m "feat: game bot AI"',
];

// Standalone typing effect extracted to its own memoized component so its
// 50-90fps re-renders (setTypedText) don't cascade to the rest of Hero,
// which would cause network-link position jumps and badge animation restarts.
const TypingEffect = memo(({ animClasses }: { animClasses: string }) => {
  const [typedText, setTypedText] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  const lineIndexRef = useRef(0);
  const charIndexRef = useRef(0);
  const phaseRef = useRef<"typing" | "pause" | "erasing">("typing");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let alive = true;

    const tick = () => {
      if (!alive) return;

      const line = typingLines[lineIndexRef.current];

      if (phaseRef.current === "typing") {
        if (charIndexRef.current < line.length) {
          charIndexRef.current += 1;
          setTypedText(line.slice(0, charIndexRef.current));
          timeoutRef.current = setTimeout(tick, 50 + Math.random() * 40);
        } else {
          phaseRef.current = "pause";
          timeoutRef.current = setTimeout(tick, 2000);
        }
      } else if (phaseRef.current === "pause") {
        phaseRef.current = "erasing";
        timeoutRef.current = setTimeout(tick, 0);
      } else {
        if (charIndexRef.current > 0) {
          charIndexRef.current -= 1;
          setTypedText(line.slice(0, charIndexRef.current));
          timeoutRef.current = setTimeout(tick, 25);
        } else {
          let next: number;
          do {
            next = Math.floor(Math.random() * typingLines.length);
          } while (next === lineIndexRef.current);
          lineIndexRef.current = next;
          phaseRef.current = "typing";
          timeoutRef.current = setTimeout(tick, 300);
        }
      }
    };

    timeoutRef.current = setTimeout(tick, 500);

    return () => {
      alive = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const cursorInterval = setInterval(() => setShowCursor((p) => !p), 530);
    return () => clearInterval(cursorInterval);
  }, []);

  return (
    <p className={`intro-tag ${animClasses}`} aria-live="polite" aria-atomic="true">
      <span className="prompt-symbol">{">"}</span> {typedText}
      <span className={`typing-cursor ${showCursor ? "visible" : ""}`}>|</span>
    </p>
  );
});
interface ProfileAvatarProps {
  registerUnlockClick: () => void;
}

const ProfileAvatar = memo(({ registerUnlockClick }: ProfileAvatarProps) => {
  const [imgReady, setImgReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Spotlight refs and states
  const mouse = useRef({ x: -999, y: -999 });
  const smooth = useRef({ x: -999, y: -999 });
  const smoothRadius = useRef(0);
  const [maskUrl, setMaskUrl] = useState<string>("none");
  const [isHovered, setIsHovered] = useState(false);
  const rafRef = useRef<number | null>(null);


  // If the image is already complete in the browser's cache on mount, set imgReady to true immediately.
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setImgReady(true);
    }
  }, []);

  // Spotlight RequestAnimationFrame loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let lastRadius = -1;
    let lastX = -1;
    let lastY = -1;

    const updateSpotlight = () => {
      if (smooth.current.x === -999) {
        smooth.current = { ...mouse.current };
      } else {
        smooth.current.x += (mouse.current.x - smooth.current.x) * 0.15;
        smooth.current.y += (mouse.current.y - smooth.current.y) * 0.15;
      }

      const targetRadius = isHovered ? 120 : 0;
      smoothRadius.current += (targetRadius - smoothRadius.current) * 0.15;

      const rDiff = Math.abs(smoothRadius.current - lastRadius);
      const xDiff = Math.abs(smooth.current.x - lastX);
      const yDiff = Math.abs(smooth.current.y - lastY);

      if (rDiff > 0.05 || xDiff > 0.05 || yDiff > 0.05) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (smoothRadius.current > 0.5) {
          const x = smooth.current.x;
          const y = smooth.current.y;
          const r = smoothRadius.current;

          const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
          gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
          gradient.addColorStop(0.4, "rgba(255, 255, 255, 1)");
          gradient.addColorStop(0.6, "rgba(255, 255, 255, 0.75)");
          gradient.addColorStop(0.75, "rgba(255, 255, 255, 0.4)");
          gradient.addColorStop(0.88, "rgba(255, 255, 255, 0.12)");
          gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }

        setMaskUrl(`url(${canvas.toDataURL()})`);
        lastRadius = smoothRadius.current;
        lastX = smooth.current.x;
        lastY = smooth.current.y;
      }

      rafRef.current = requestAnimationFrame(updateSpotlight);
    };

    rafRef.current = requestAnimationFrame(updateSpotlight);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isHovered]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouse.current = {
      x: ((e.clientX - rect.left) / rect.width) * 380,
      y: ((e.clientY - rect.top) / rect.height) * 380,
    };
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsHovered(true);
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 380;
    const y = ((e.clientY - rect.top) / rect.height) * 380;
    mouse.current = { x, y };
    if (smooth.current.x === -999) smooth.current = { x, y };
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      ref={containerRef}
      className="profile-image-circle"
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      // onTouchStart={handleTouchStart}
      // onTouchMove={handleTouchMove}
      // onTouchEnd={handleTouchEnd}
      // onTouchCancel={handleTouchEnd}
    >
      <canvas
        ref={canvasRef}
        width={380}
        height={380}
        style={{ display: "none" }}
      />
      <div
        className="profile-image-placeholder-shimmer"
        style={{
          opacity: imgReady ? 0 : 1,
          pointerEvents: imgReady ? "none" : "auto",
        }}
      >
        <div className="shimmer-pulse-circle"></div>
        <span className="placeholder-initials">MA</span>
      </div>
      <picture
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          opacity: imgReady ? 1 : 0,
          transition: "opacity 0.4s ease-in-out",
        }}
      >
        <source srcSet="/avatars/Profile/mohit-arora.webp" type="image/webp" />
        <source srcSet="/avatars/Profile/mohit-arora.jpg" type="image/jpeg" />
        <img
          ref={imgRef}
          src={images.profile}
          alt="Mohit Arora"
          className="profile-image"
          draggable="false"
          loading="eager"
          fetchPriority="high"
          width={372}
          height={372}
          onClick={registerUnlockClick}
          onLoad={() => setImgReady(true)}
          onError={(e) => {
            const img = e.currentTarget;
            const list = [images.profile, ...images.profileFallbacks];
            const currentIdx = list.findIndex((item) => {
              if (item.startsWith("http://") || item.startsWith("https://")) {
                return img.src === item;
              }
              try {
                return new URL(img.src).pathname === item;
              } catch {
                return img.src.endsWith(item);
              }
            });
            const next = currentIdx >= 0 ? list[currentIdx + 1] : undefined;
            if (next) {
              img.src = next;
            }
          }}
        />
      </picture>

      {/* Spotlight revealed cybernetic robot overlay */}
      <div
        className="profile-image-reveal"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          WebkitMaskImage: maskUrl,
          maskImage: maskUrl,
          WebkitMaskSize: "100% 100%",
          maskSize: "100% 100%",
          opacity: imgReady ? 1 : 0,
        }}
      >
        <img
          src={images.profileRobot}
          alt="Mohit Arora Robot"
          className="profile-image"
          draggable="false"
          width={372}
          height={372}
        />
      </div>

      <div className="image-overlay"></div>
    </div>
  );
});

const Hero = memo(() => {
  // Index into the rotating roles array
  const [roleIndex, setRoleIndex] = useState(0);
  const [animState, setAnimState] = useState<"enter" | "exit">("enter");
  // Tracks which network icon is currently hovered for tooltip display
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);
  const { config: debug, registerUnlockClick } = useDebug();
  // Responsive detection for mobile layout adjustments (network link positioning).
  // Initial read uses SSR guard (typeof window !== 'undefined'), resize listener handles updates.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const [imgReady, setImgReady] = useState(false);
  const [showGitHubStats, setShowGitHubStats] = useState(false);

  useEffect(() => {
    if (debug.GITHUB_STATS) {
      if ("requestIdleCallback" in window) {
        const handle = window.requestIdleCallback(() => setShowGitHubStats(true));
        return () => window.cancelIdleCallback(handle);
      } else {
        const timer = setTimeout(() => setShowGitHubStats(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [debug.GITHUB_STATS]);

  // Resize listener for mobile detection; role cyclic changes are handled in a separate effect
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", checkMobile);
    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Separate effect for role cycling that respects enter/exit state transitions.
  // Schedules the exit animation 3 seconds after the current role finishes entering.
  useEffect(() => {
    if (animState === "enter") {
      const timer = setTimeout(() => {
        setAnimState("exit");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [roleIndex, animState]);

  // Preloads the profile image so the image circle stays invisible until at least
  // one source (WebP or JPG) is ready, avoiding the empty-circle flash.
  // Whichever format resolves first will trigger the reveal instantly.
  useEffect(() => {
    let cancelled = false;
    let finished = false;

    const onFinish = () => {
      if (cancelled || finished) return;
      finished = true;
      setImgReady(true);
    };

    // Preload WebP
    const preloadWebp = new Image();
    preloadWebp.onload = onFinish;
    preloadWebp.onerror = () => {
      if (preloadJpg.complete || preloadJpg.naturalWidth > 0) {
        onFinish();
      }
    };
    preloadWebp.src = images.profile;

    // Preload JPG
    const preloadJpg = new Image();
    preloadJpg.onload = onFinish;
    preloadJpg.onerror = () => {
      if (preloadWebp.complete || preloadWebp.naturalWidth > 0) {
        onFinish();
      }
    };
    preloadJpg.src = "/avatars/Profile/mohit-arora.jpg";

    // Safety timeout (2s) to guarantee the container is revealed
    const safetyTimer = setTimeout(onFinish, 2000);

    return () => {
      cancelled = true;
      clearTimeout(safetyTimer);
    };
  }, []);

  // Stable animation type + duration derived from debug config (computed once).
  // The CSS class key uses toFixed(1) (e.g. "05" for 0.5s) to match the CSS rules.
  const animTypeClass = useMemo(() => {
    const isAnimActive = debug.ANIMATIONS && debug.ANIM_SECTIONS?.HERO;
    if (!isAnimActive) return "";
    const animType = debug.ANIM_TYPE?.HERO ?? "fadeInUp";
    const dur = debug.ANIM_SPEED?.HERO ?? 0.5;
    const durKey = dur.toFixed(1).replace(".", "");
    return `hero-anim-type-${animType} hero-anim-dur-${durKey}`;
  }, [debug.ANIMATIONS, debug.ANIM_SECTIONS?.HERO, debug.ANIM_TYPE?.HERO, debug.ANIM_SPEED?.HERO]);

  // Returns CSS animation classes with the imgReady gate.
  // When the profile image hasn't loaded yet, returns "hero-anim-hidden"
  // (opacity: 0) to prevent the text from appearing before the image.
  // Once ready, returns the full animation class set with staggered delay.
  const getAnimClasses = (delay: number) => {
    if (!animTypeClass) return "";
    if (!imgReady) return "hero-anim-hidden";
    const key = (Math.round(delay * 100) / 100).toFixed(2).replace(".", "");
    return `hero-anim-active ${animTypeClass} hero-anim-delay-${key}`;
  };

  const name = "Mohit Arora";

  return (
    <section className="hero">
      <div className="container hero-container">
        <div className="hero-content">
          <div className="hero-text">
            {/* Terminal prompt with typing effect and blinking cursor */}
            <TypingEffect animClasses={getAnimClasses(0.2)} />

            <div className="hero-name-container">
              {name.split("").map((char, i) => (
                <span
                  key={i}
                  className={`kinetic-char-wrapper ${getAnimClasses(0.5 + i * 0.05)}`}
                >
                  <span className="kinetic-char">
                    {char === " " ? "\u00A0" : char}
                  </span>
                </span>
              ))}
            </div>

            {/* Rotating role title with exit/entrance animation via state sequencing.
                Runs compositor-optimized CSS animations for enter and exit transitions. */}
            <div className={`hero-role-container ${getAnimClasses(0.6)}`}>
              <div
                key={roleIndex}
                className={`hero-role role-${animState}`}
                onAnimationEnd={() => {
                  if (animState === "exit") {
                    setRoleIndex((prev) => (prev + 1) % roles.length);
                    setAnimState("enter");
                  }
                }}
              >
                {roles[roleIndex]}
              </div>
            </div>

            <h3 className="hero-specialty">
              <span className={getAnimClasses(0.75)}>Full-Stack Developer | Innovator</span>
              <HiddenDevComment {...devComments.hero} />
            </h3>

            <p className={`hero-desc ${getAnimClasses(0.9)}`}>
              4.5+ years shipping at scale - from <span>multiplayer games with 25M+ downloads</span> to
              <span> AI-driven learning tools</span>. I build backend systems in <span>Java + Spring Boot</span>, interfaces in{" "}
              <span>React + TypeScript</span>, and immersive experiences in <span>Unity + C#</span>.
            </p>

            <div className={`hero-cta ${getAnimClasses(1.05)}`}>
              <a href="#experience" className="btn-primary">
                See the impact
              </a>
              <a href="#projects" className="btn-secondary">
                Browse projects →
              </a>
            </div>
          </div>

          <div className={`hero-visuals ${getAnimClasses(0.15)}`}>
            <div className="profile-image-container">
              <ProfileAvatar registerUnlockClick={registerUnlockClick} />
              {/* Network links positioned in a curved arc (bottom-left quadrant) around the profile image.
                  Trigonometry-based: cos * 50% and sin * 50% position at the circle's edge.
                  var(--network-offset, 40px) = combined border-radius + half icon size + spacing.
                  Angles (170, 152.5, 135, 117.5, 100 degrees) tune spacing in bottom-left quadrant.
                  Mobile adjustments (isMobile ? 175 : 170) compensate for smaller screen.
                  tooltipOffset per item fine-tunes horizontal alignment for icons near screen edge. */}              <div className="hero-networks-curved">
                {[
                  {
                    name: "LinkedIn",
                    icon: <FaLinkedin aria-hidden="true" />,
                    link: LINKS.LINKEDIN,
                    angle: isMobile ? 175 : 170,
                    tooltipOffset: isMobile ? "12px" : "0px",
                  },
                  {
                    name: "GitHub",
                    icon: <FaGithub aria-hidden="true" />,
                    link: LINKS.GITHUB,
                    angle: isMobile ? 155 : 152.5,
                    tooltipOffset: isMobile ? "6px" : "0px",
                  },
                  { name: "LeetCode", icon: <SiLeetcode aria-hidden="true" />, link: LINKS.LEETCODE, angle: 135, tooltipOffset: "0px" },
                  {
                    name: "GeeksForGeeks",
                    icon: <SiGeeksforgeeks aria-hidden="true" />,
                    link: LINKS.GEEKSFORGEEKS,
                    angle: isMobile ? 115 : 117.5,
                    tooltipOffset: "0px",
                  },
                  {
                    name: "TakeUForward",
                    icon: <FaCode aria-hidden="true" />,
                    link: LINKS.TAKEU_FORWARD,
                    angle: isMobile ? 95 : 100,
                    tooltipOffset: "0px",
                  },
                ].map((item, i) => {
                  // Position strictly in 3rd quadrant (bottom-left)
                  // To ensure it touches the circle on all screen sizes,
                  // we calculate x and y as a combination of 50% (radius) + 40px (border + half icon + spacing).
                  const radian = (item.angle * Math.PI) / 180;
                  const cos = Math.cos(radian);
                  const sin = Math.sin(radian);
                  const xCalc = `calc(50% + ${cos * 50}% + var(--network-offset, 40px) * ${cos})`;
                  const yCalc = `calc(50% + ${sin * 50}% + var(--network-offset, 40px) * ${sin})`;
                  return (
                    <a
                      key={i}
                      style={
                        {
                          left: xCalc,
                          top: yCalc,
                          "--tooltip-offset": item.tooltipOffset || "0px",
                        } as React.CSSProperties
                      }
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`network-link-curved ${hoveredIcon === item.name ? "active-hover" : ""}`}
                      aria-label={item.name}
                      onMouseEnter={() => {
                        setHoveredIcon(item.name);
                      }}
                      onMouseLeave={() => {
                        setHoveredIcon(null);
                      }}
                    >
                      <span className="network-link-bg"></span>
                      <span className="network-link-icon">{item.icon}</span>
                      <span className={`network-tooltip ${hoveredIcon === item.name ? "is-visible" : ""}`}>{item.name}</span>
                    </a>
                  );
                })}
              </div>

              {/* Floating stat badges with continuous y-axis oscillation animations.
                  CSS keyframes handle the floating behavior to optimize main-thread execution. */}
              <div className="floating-badge badge-left">
                <span className="badge-icon">🎮</span>
                <div className="badge-text">
                  <strong>25M+</strong>
                  <span>Downloads</span>
                </div>
              </div>
              <div className="floating-badge badge-right">
                <span className="badge-icon">💻</span>
                <div className="badge-text">
                  <strong>4.5+ Years</strong>
                  <span>Experience</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {debug.GITHUB_STATS && (
          <div className="gh-stats-container">
            {showGitHubStats ? (
              <ErrorBoundary fallback={<></>} sectionName="GitHub Stats">
                <Suspense fallback={null}>
                  <LazyGitHubStats />
                </Suspense>
              </ErrorBoundary>
            ) : (
              <div className="gh-stats-loader-placeholder" />
            )}
          </div>
        )}
      </div>
    </section>
  );
});

export default Hero;